import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { portalApi } from '../lib/portalApi';

const AuthContext = createContext(null);

function normalizeUser(raw) {
  if (!raw) return null;
  const roles = raw.roles?.length
    ? raw.roles
    : raw.role === 'ADMIN' || raw.role === 'SUB_ADMIN'
      ? ['admin', raw.role === 'ADMIN' ? 'superadmin' : null].filter(Boolean)
      : raw.role === 'STARTUP_FOUNDER'
        ? ['founder']
        : raw.role === 'CXO' || raw.role === 'MENTOR'
          ? ['cxo', 'mentor']
          : ['user'];
  return {
    ...raw,
    roles,
    fullName: raw.fullName || raw.name,
    startups: raw.startups || [],
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('innovher_token');
    const refresh = localStorage.getItem('innovher_refresh');
    if (!token && !refresh) {
      setLoading(false);
      return;
    }
    try {
      let me;
      try {
        me = await portalApi.me();
      } catch {
        if (!refresh) throw new Error('No session');
        await portalApi.refresh();
        me = await portalApi.me();
      }
      const u = normalizeUser(me);
      setUser(u);
      localStorage.setItem('innovher_user', JSON.stringify(u));
    } catch {
      localStorage.removeItem('innovher_token');
      localStorage.removeItem('innovher_refresh');
      localStorage.removeItem('innovher_user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const data = await portalApi.login(email, password);
    const u = normalizeUser(data.user);
    if (u.roles.includes('founder') || u.role === 'STARTUP_FOUNDER') {
      u.home = '/startup-dashboard';
    } else {
      u.home = data.home && data.home !== '/guru' ? data.home : (u.roles.includes('mentor') || u.roles.includes('cxo')) ? '/mentor-dashboard' : data.home;
    }
    setUser(u);
    localStorage.setItem('innovher_user', JSON.stringify(u));
    return u;
  };

  const logout = async () => {
    await portalApi.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, reload: loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function getDashboardPath(userOrRole) {
  if (typeof userOrRole === 'string') {
    if (['ADMIN', 'SUB_ADMIN'].includes(userOrRole)) return '/admin';
    if (userOrRole === 'STARTUP_FOUNDER') return '/startup-dashboard';
    if (userOrRole === 'INVESTOR') return '/investor-dashboard';
    if (['CXO', 'MENTOR'].includes(userOrRole)) return '/mentor-dashboard';
    return '/login';
  }
  const user = userOrRole;
  if (!user) return '/login';
  const roles = user.roles || [];
  if (roles.includes('admin') || roles.includes('superadmin') || ['ADMIN', 'SUB_ADMIN'].includes(user.role)) {
    return '/admin';
  }
  if (roles.includes('founder') || user.role === 'STARTUP_FOUNDER') {
    return '/startup-dashboard';
  }
  if (roles.includes('cxo') || roles.includes('mentor') || ['CXO', 'MENTOR'].includes(user.role)) {
    return '/mentor-dashboard';
  }
  if (user.role === 'INVESTOR') return '/investor-dashboard';
  return '/login';
}

export function isAdminUser(user) {
  if (!user) return false;
  const roles = user.roles || [];
  return (
    roles.includes('admin') ||
    roles.includes('superadmin') ||
    ['ADMIN', 'SUB_ADMIN'].includes(user.role)
  );
}

export function isFounderUser(user) {
  if (!user) return false;
  const roles = user.roles || [];
  return roles.includes('founder') || user.role === 'STARTUP_FOUNDER';
}

export function isMentorUser(user) {
  if (!user) return false;
  const roles = user.roles || [];
  return (
    roles.includes('mentor') ||
    roles.includes('cxo') ||
    ['CXO', 'MENTOR'].includes(user.role)
  );
}
